import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { PrepaidCardTransactionRow, PrepaidCardTransactionType } from '@models/prepaidCard';
import dayjs from 'dayjs';
import React, { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import CancelIcon from './CancelIcon';
import ChargeIcon from './ChargeIcon';

export interface Props extends HTMLAttributes<HTMLDivElement>{
    isFirst?: boolean;
    transaction: PrepaidCardTransactionRow;
}

function LogItem({
    transaction, isFirst, ...rest
}: Props) {
    const {
        amount, createdAt, typeName, type,
    } = transaction;
    const { t } = useTranslation('prepaidCard');
    const color: Record<PrepaidCardTransactionType, string> = {
        CANCEL_USE: colors.gray5,
        USED: colors.red,
        CHARGE_CANCELED: colors.red,
        CHARGED: colors.green,
        REFUND_BALANCE: colors.red,
        RETURN_PURCHASE: colors.red,
    };
    const getBadge = (transactionType: PrepaidCardTransactionType) => {
        const badges: Record<PrepaidCardTransactionType, ReactNode> = {
            CHARGE_CANCELED: <CancelIcon/>,
            CHARGED: <ChargeIcon/>,
            USED: <TypeBadge>
                <Text size='s' fontWeight='bold' color={colors.gray6}>
                    {t('log.type.payment')}
                </Text>
            </TypeBadge>,
            CANCEL_USE: <TypeBadge isCancel>
                <Text size='s' fontWeight='bold' color={colors.primary}>
                    {t('log.type.cancelUse')}
                </Text>
            </TypeBadge>,
            REFUND_BALANCE: <TypeBadge isCancel>
                <Text size='s' fontWeight='bold' color={colors.primary}>
                    {t('log.type.refundBalance')}
                </Text>
            </TypeBadge>,
            RETURN_PURCHASE: <TypeBadge isCancel>
                <Text size='s' fontWeight='bold' color={colors.primary}>
                    {t('log.type.cancelPayment')}
                </Text>
            </TypeBadge>,
        };
        return badges[transactionType];
    };
    return (
        <Wrapper isFirst={isFirst} {...rest} >
            <Container aria-label='prepaid-card-log'>
                <Contents spacing={12}>
                    {getBadge(type)}
                    <Row fullWidth
                        justify='space-between'>
                        <Text size='xl' fontWeight='bold'>
                            {typeName}
                        </Text>
                        <Text size='xl' fontWeight='bold' color={color[type]}>
                            {type === 'CHARGED' ? '+' : '-'}
                            {amount.toLocaleString()}{t('currencyUnit')}
                        </Text>
                    </Row>
                </Contents>
                <Footer justify='center'
                    align='center'
                    fullWidth>
                    <Text color={colors.gray6} >
                        {dayjs(createdAt).format('YYYY.MM.DD  HH:mm:ss')}
                    </Text>
                </Footer>
            </Container>
        </Wrapper>
    );
}

const Wrapper = styled.div<{
    isFirst?: boolean;
}>`
    padding: 9px 20px;
    padding-top: ${({ isFirst }) => (isFirst ? '29px' : '9px')};
`;

const Container = styled.div`
    border: 1px solid var(--color-gray3);
    border-radius: 8px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.05);
    width: 100%;
`;

const Contents = styled(Column)`
    padding: 16px 12px;
`;

const Footer = styled(Row)`
    border-top: 1px solid var(--color-gray3);
    padding: 8px 0;
`;

const TypeBadge = styled.div<{
    isCancel?: boolean;
}>`
    border: 1px solid;
    border-color: ${({ isCancel }) => (isCancel ? 'var(--color-primary)' : 'var(--color-gray6)')};
    border-radius: 15px;
    padding: 1px 7px;
`;

export default LogItem;
